// src/features/criador/CodigoCriadorEtapa.jsx
//
// Etapa "Tem um código de criador?" (handoff C4) — logo depois do código do
// WhatsApp de um cadastro NOVO, antes da tela de boas-vindas (3 de 4 no
// stepper da entrada). Pular é sempre possível.
//
// Estados: vazio · válido (nome/foto do criador + "Você ganhou 150 VitaCoins e
// 7 dias de premium") · inválido · código próprio. Válido → celebração do
// presente (+150 / 7 dias) → segue.
//
// Quem decide tudo é o backend (POST /afiliados/indicacao): dono do código,
// autoindicação por uid/CPF/telefone/aparelho, antifraude e os benefícios.
// Aqui só se mostra o que ele respondeu. Sem nome em display string: os campos
// vêm estruturados (status, criador, beneficios).

import React, { useEffect, useState } from "react";
import Icon from "../../design/icons/Icon";
import SeloCriador from "./SeloCriador";
import { buscarCriador, lerCupomGuardado, normalizarCodigo, usarCodigo } from "./cupomCriador";
import { obterDeviceId, plataformaAtual } from "./dispositivo";
import S from "./Criador.module.css";

const Voltar = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6" />
  </svg>
);
const Check = ({ tam = 14 }) => (
  <svg width={tam} height={tam} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const Alerta = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4M12 16h.01" />
  </svg>
);

function AvatarCriador({ criador }) {
  return (
    <div className={S.avatarSelo}>
      {criador?.FOTO_URL ? (
        <img src={criador.FOTO_URL} alt="" className={S.avatar} />
      ) : (
        <span className={S.avatar}>{String(criador?.NOME || "?").charAt(0).toUpperCase()}</span>
      )}
      <span className={S.seloCanto}><SeloCriador size={16} glow={false} /></span>
    </div>
  );
}

function dataFimPremium(fimIso, dias) {
  const d = fimIso ? new Date(fimIso) : new Date(Date.now() + Number(dias || 7) * 86400000);
  const valida = !Number.isNaN(d.getTime()) ? d : new Date(Date.now() + Number(dias || 7) * 86400000);
  return valida.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", timeZone: "America/Sao_Paulo" });
}

/**
 * Props:
 *   user         usuário do Firebase Auth (para o ID token)
 *   codigoInicial  código vindo de link/deep link/área de transferência
 *   onVoltar     volta para a etapa anterior (opcional)
 *   onConcluir   ({usou, resultado}) — segue o cadastro
 */
export default function CodigoCriadorEtapa({ user, codigoInicial = "", onVoltar, onConcluir }) {
  const inicial = normalizarCodigo(codigoInicial || lerCupomGuardado()?.codigo || "");
  const [codigo, setCodigo] = useState(inicial);
  const [previa, setPrevia] = useState(null); // {NOME, FOTO_URL} do GET público
  const [estado, setEstado] = useState(inicial ? "checando" : "vazio"); // vazio|checando|valido|invalido|proprio|enviando
  const [mensagem, setMensagem] = useState("");
  const [resultado, setResultado] = useState(null);
  const [presente, setPresente] = useState(false);
  const preenchidoPeloLink = !!inicial;

  // Código que chega DEPOIS de a tela abrir (área de transferência do iOS ou
  // Install Referrer do Android lidos em paralelo): preenche se estiver vazio.
  useEffect(() => {
    const ouvir = (ev) => {
      const c = normalizarCodigo(ev?.detail?.codigo);
      if (c) setCodigo((atual) => atual || c);
    };
    window.addEventListener("lesvita:cupom-criador", ouvir);
    return () => window.removeEventListener("lesvita:cupom-criador", ouvir);
  }, []);

  // Prévia do dono enquanto digita (GET público, sem efeito colateral).
  useEffect(() => {
    const c = normalizarCodigo(codigo);
    if (c.length < 3) {
      setPrevia(null);
      setEstado((e) => (e === "proprio" ? e : "vazio"));
      return undefined;
    }
    let vivo = true;
    setEstado("checando");
    const t = setTimeout(async () => {
      const d = await buscarCriador(c);
      if (!vivo) return;
      if (d?.VALIDO) {
        setPrevia(d);
        setEstado("valido");
        setMensagem("");
      } else {
        setPrevia(null);
        setEstado(d?.REDE ? "vazio" : "invalido");
        setMensagem(d?.REDE ? "" : "Código não encontrado. Confere com quem te indicou ou pula esta etapa.");
      }
    }, 350);
    return () => { vivo = false; clearTimeout(t); };
  }, [codigo]);

  const continuar = async () => {
    if (estado !== "valido") return;
    setEstado("enviando");
    const deviceId = await obterDeviceId();
    const r = await usarCodigo(user, codigo, { origem: "APP", deviceId, plataforma: plataformaAtual() });
    setResultado(r);
    if (r?.status === "atribuida") {
      setPresente(true);
      return;
    }
    if (r?.status === "ja_atribuida" || r?.status === "pessoa_duplicada") {
      // Um código por pessoa, para sempre: segue sem presente, sem drama.
      onConcluir?.({ usou: false, resultado: r });
      return;
    }
    if (r?.status === "proprio") {
      setEstado("proprio");
      setMensagem("Esse é o seu código de criador. Autoindicação não vale, mas seu premium de Criador já está ativo.");
      return;
    }
    if (r?.status === "erro_rede") {
      setEstado("valido");
      setMensagem("Sem conexão agora. Tenta de novo ou pula esta etapa.");
      return;
    }
    setEstado("invalido");
    setMensagem("Código não encontrado. Confere com quem te indicou ou pula esta etapa.");
  };

  if (presente) {
    const b = resultado?.beneficios || {};
    const nome = resultado?.criador?.NOME || previa?.NOME || "";
    const vitacoins = b.VITACOINS ?? previa?.VITACOINS ?? 150;
    const dias = b.PREMIUM_DIAS ?? previa?.PREMIUM_DIAS ?? 7;
    return (
      <div className={S.telaCheia}>
        <div className={S.brilhoOuro} />
        <div className={S.centro}>
          <div className={S.presente}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
            </svg>
          </div>
          <div className={`${S.bloco} ${S.entrarAtrasado}`}>
            <span className={`${S.caps} ${S.capsOuro}`}>{nome ? `PRESENTE DE ${nome.toUpperCase()}` : "PRESENTE"}</span>
            <h1 className={`${S.titulo} ${S.tituloGrande}`}>Você começou com vantagem</h1>
          </div>
          <div className={S.ganhos}>
            <div className={S.ganho}>
              {/* O símbolo oficial da VitaCoin, grande — não um hexágono de CSS. */}
              <Icon name="coin" size={40} />
              <strong>+{vitacoins}</strong>
              <span>VitaCoins</span>
            </div>
            <div className={S.ganho}>
              <span className={S.estrela}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.3 5.9 20.6l1.4-6.8L2.2 9.1l6.9-.8z" />
                </svg>
              </span>
              <strong>{dias} dias</strong>
              <span>de premium</span>
            </div>
          </div>
          <span className={`${S.lead} ${S.entrarMaisAtrasado}`}>
            6 scans por dia, ScanBody, mapa muscular, VITA e app sem anúncios até {dataFimPremium(b.PREMIUM_FIM, dias)}.
          </span>
        </div>
        <div className={S.rodape}>
          <button type="button" className={`${S.btn} ${S.btnPrimario}`} onClick={() => onConcluir?.({ usou: true, resultado })}>
            Continuar
          </button>
        </div>
      </div>
    );
  }

  const ok = estado === "valido" || estado === "enviando";
  const erro = estado === "invalido" || estado === "proprio";

  return (
    <div className={S.telaCheia}>
      <div className={S.conteudo}>
        <div className={S.topo}>
          {onVoltar ? (
            <button type="button" className={S.voltar} onClick={onVoltar} aria-label="Voltar"><Voltar /></button>
          ) : <span />}
          <div className={S.progresso} aria-label="Passo 3 de 4">
            {[0, 1, 2, 3].map((i) => <span key={i} className={`${S.segmento} ${i < 3 ? S.segmentoOn : ""}`} />)}
          </div>
          <button type="button" className={S.pular} onClick={() => onConcluir?.({ usou: false, resultado: null })}>Pular</button>
        </div>
        <div className={S.bloco}>
          <h1 className={S.titulo}>Tem um código de criador?</h1>
          <p className={S.lead}>Se alguém te indicou o LesVita, coloca o código aqui. Você ganha na hora.</p>
        </div>
        <div className={S.bloco}>
          <label className={`${S.codigoBox} ${ok ? S.codigoOk : ""} ${erro ? S.codigoErro : ""}`}>
            <input
              className={S.codigoEntrada}
              value={codigo}
              onChange={(e) => { setCodigo(normalizarCodigo(e.target.value)); setMensagem(""); }}
              placeholder="Digite o código"
              aria-label="Código de criador"
              autoCapitalize="characters"
              autoComplete="off"
              maxLength={24}
              disabled={estado === "enviando"}
            />
            {ok ? <span className={S.checkVerde}><Check /></span> : null}
            {erro ? <span className={S.iconeErro}><Alerta /></span> : null}
          </label>
          {ok && previa ? (
            <>
              <div className={S.cardCriador}>
                <AvatarCriador criador={previa} />
                <div className={S.cardCriadorTexto}>
                  Código de {previa.NOME}
                  <span>Você ganhou {previa.VITACOINS || 150} VitaCoins e {previa.PREMIUM_DIAS || 7} dias de premium</span>
                </div>
              </div>
              {preenchidoPeloLink ? (
                <span className={S.dica}>Já veio pelo link de {previa.NOME}? O código foi preenchido automaticamente.</span>
              ) : null}
            </>
          ) : null}
          {mensagem ? <span className={S.erro}>{mensagem}</span> : null}
        </div>
        <div className={S.espaco} />
      </div>
      <div className={S.rodape}>
        <button type="button" className={`${S.btn} ${S.btnFluxo}`} disabled={!ok || estado === "enviando"} onClick={continuar}>
          {estado === "enviando" ? <span className={S.giro} /> : "Continuar"}
        </button>
      </div>
    </div>
  );
}
